"use client"

import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from "react"
import { ExternalLink } from "lucide-react"

export interface HeroReview {
  author_name: string
  text: string
}

interface HeroReviewsCardProps {
  reviews: HeroReview[]
  rating: number
  displayRating: string
  reviewCount: number
  reviewsUrl: string
}

const ROTATION_MS = 6000
const STAR_PATH =
  "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, rating - i))
        return (
          <div key={i} className="relative h-[18px] w-[18px]">
            <svg className="h-[18px] w-[18px] fill-white/20" viewBox="0 0 24 24">
              <path d={STAR_PATH} />
            </svg>
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <svg className="h-[18px] w-[18px] fill-amber-400" viewBox="0 0 24 24">
                <path d={STAR_PATH} />
              </svg>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function GoogleMark() {
  return (
    <svg className="h-6 w-6 flex-shrink-0" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  )
}

export default function HeroReviewsCard({
  reviews,
  rating,
  displayRating,
  reviewCount,
  reviewsUrl,
}: HeroReviewsCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  // The first quote paints immediately (no fade-in) so it doesn't delay Largest Contentful Paint.
  const [hasRotated, setHasRotated] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [userNavigated, setUserNavigated] = useState(false)
  const cardRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    setPrefersReducedMotion(query.matches)
    const onChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  const isAutoRotating = reviews.length > 1 && !isHovered && !isFocused && !prefersReducedMotion

  useEffect(() => {
    if (!isAutoRotating) return
    const interval = setInterval(() => {
      setHasRotated(true)
      setCurrentIndex((prev) => (prev + 1) % reviews.length)
    }, ROTATION_MS)
    return () => clearInterval(interval)
  }, [isAutoRotating, reviews.length])

  const goTo = (index: number) => {
    setHasRotated(true)
    setUserNavigated(true)
    setCurrentIndex((index + reviews.length) % reviews.length)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (reviews.length <= 1) return
    if (event.key === "ArrowRight") {
      event.preventDefault()
      goTo(currentIndex + 1)
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      goTo(currentIndex - 1)
    }
  }

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!cardRef.current?.contains(event.relatedTarget as Node | null)) setIsFocused(false)
  }

  const currentReview = reviews[currentIndex] ?? reviews[0]

  return (
    <section
      ref={cardRef}
      aria-roledescription="carousel"
      aria-label="Customer reviews from Google"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className="w-full max-w-[460px] rounded-[20px] border border-white/[0.18] bg-[rgba(58,14,20,0.88)] px-5 pb-4 pt-5 shadow-[0_12px_35px_rgba(0,0,0,0.18)] supports-[backdrop-filter]:bg-white/[0.09] supports-[backdrop-filter]:backdrop-blur-[16px] sm:px-7 sm:pb-4 sm:pt-5"
    >
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center justify-center gap-2.5">
          <GoogleMark />
          <span className="font-heading text-[23px] font-bold leading-none tracking-tight text-white">
            {displayRating}
          </span>
          <RatingStars rating={rating} />
        </div>
        <p className="font-sans text-[13px] text-white/75 sm:text-sm">
          <span className="sr-only">{`Rated ${displayRating} out of 5, `}</span>
          {`Based on ${reviewCount.toLocaleString("en-US")} Google reviews`}
        </p>
      </div>

      <div className="mx-auto my-3 h-px w-12 bg-white/15" aria-hidden="true" />

      <div
        aria-live={userNavigated ? "polite" : "off"}
        aria-atomic="true"
        className="flex flex-col items-center"
      >
        <div
          key={currentIndex}
          role="group"
          aria-roledescription="slide"
          aria-label={`Review ${currentIndex + 1} of ${reviews.length}`}
          className={`${hasRotated ? "motion-safe:animate-review-fade " : ""}flex w-full flex-col items-center`}
        >
          <blockquote className="flex h-[4.9rem] items-center justify-center sm:h-[5.2rem]">
            <p className="line-clamp-3 text-pretty text-center font-sans text-[15px] leading-relaxed text-white/95 sm:text-base">
              &ldquo;{currentReview.text}&rdquo;
            </p>
          </blockquote>
          <p className="mt-2 line-clamp-1 font-sans text-[13px] font-semibold tracking-wide text-amber-300">
            &mdash; {currentReview.author_name}
          </p>
        </div>
      </div>

      {reviews.length > 1 && (
        <div className="mt-1.5 flex items-center justify-center gap-[4px]">
          {reviews.map((review, i) => {
            const isActive = i === currentIndex
            return (
              <button
                key={`${review.author_name}-${i}`}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Show review ${i + 1} of ${reviews.length}`}
                aria-current={isActive ? "true" : undefined}
                className="group flex h-[24px] min-w-[24px] items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/80"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none ${
                    isActive ? "w-5 bg-amber-300" : "w-1.5 bg-white/35 group-hover:bg-white/60"
                  }`}
                />
              </button>
            )
          })}
        </div>
      )}

      <div className="mt-1 flex justify-center">
        <a
          href={reviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded font-sans text-[13px] text-white/75 underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/80"
        >
          Read our reviews on Google
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>
    </section>
  )
}
